/**
 * DeleteAccountConfirmModal Component
 * Two-step "are you sure" confirmation for permanently deleting a member's account
 * and all of their data. Requires typing the account email to confirm intent.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const overlayStyle = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  background: 'rgba(0, 0, 0, 0.78)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 2147483647,
  padding: '24px',
  overflowY: 'auto',
  boxSizing: 'border-box',
  backdropFilter: 'blur(1px)',
};

const cardStyle = {
  width: '100%',
  maxWidth: '620px',
  minHeight: '240px',
  background: '#ffffff',
  borderRadius: '14px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.24)',
  padding: '28px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: 'auto',
  boxSizing: 'border-box',
};

const titleStyle = {
  margin: '0 0 10px 0',
  color: '#1f2937',
  fontSize: '24px',
  fontWeight: '700',
};

const textStyle = {
  margin: '0 0 16px 0',
  color: '#4b5563',
  fontSize: '16px',
  lineHeight: 1.5,
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '15px',
  marginBottom: '18px',
  boxSizing: 'border-box',
};

const actionRowStyle = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
};

const btnBase = {
  borderRadius: '8px',
  border: '1px solid transparent',
  padding: '12px 16px',
  fontSize: '15px',
  fontWeight: '600',
  cursor: 'pointer',
};

const secondaryBtn = {
  ...btnBase,
  background: '#ffffff',
  borderColor: '#d1d5db',
  color: '#374151',
};

const dangerBtn = {
  ...btnBase,
  background: '#b91c1c',
  borderColor: '#991b1b',
  color: '#ffffff',
};

const disabledBtn = {
  opacity: 0.65,
  cursor: 'not-allowed',
};

const DeleteAccountConfirmModal = ({
  isOpen,
  userEmail,
  isSubmitting,
  error,
  onCancel,
  onConfirm,
}) => {
  const [confirmationText, setConfirmationText] = useState('');

  if (!isOpen) return null;

  const canConfirm = confirmationText.trim().toLowerCase() === (userEmail || '').trim().toLowerCase();

  return createPortal(
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="delete-account-modal-title">
      <div style={cardStyle}>
        <h3 id="delete-account-modal-title" style={titleStyle}>Are you sure you want to delete your account?</h3>
        <p style={textStyle}>
          This will <strong>permanently erase</strong> your account and all of your data,
          including saved receipts and bookings. This action cannot be undone.
        </p>
        <p style={textStyle}>
          Type your email address (<strong>{userEmail}</strong>) below to confirm.
        </p>
        <input
          type="text"
          style={inputStyle}
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          placeholder="Type your email to confirm"
          disabled={isSubmitting}
        />
        {error && (
          <p style={{ color: '#b91c1c', fontSize: '13px', margin: '-8px 0 16px 0' }}>{error}</p>
        )}
        <div style={actionRowStyle}>
          <button type="button" style={secondaryBtn} onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            style={!canConfirm || isSubmitting ? { ...dangerBtn, ...disabledBtn } : dangerBtn}
            onClick={onConfirm}
            disabled={!canConfirm || isSubmitting}
          >
            {isSubmitting ? 'Deleting account...' : 'Yes, permanently delete my account'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeleteAccountConfirmModal;
